import React, { useEffect, useState, useRef, forwardRef } from "react";
import { toast } from "react-toastify";
import { useSave, useDetail, useQuery, useSet, useCount } from "seed/gql";
import { useGet, usePost } from "seed/api";
import { SET_EXECUTION, SAVE_CHAT_MESSAGE } from "seed/gql/queries";
import { bodyContentMapper } from "components/studio/board/nodes/Node.lib";
import { useUserPermissions } from "components/util/helpers/UserPermissions";
import l from "components/util/l";
import View from "components/presentation/chat/Chat.view";

const Chat = forwardRef(({
  notesRef,
  flow,
  chat,
  selectedNote,
  currentChat,
  chatSessionId,
  removePadding = false,
  isExecutionChat,
  onClickTab = () => { },
  onClickResumeChat = () => { },
  onExecutionMessage = () => { }
}, ref) => {

  const subFormRef = useRef();
  const chatRef = useRef();
  const executionRef = useRef();
  const isDeveloper = sessionStorage.getItem("is_developer") == "true";
  const [scrollToBottom, setScrollToBottom] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isExecutionDetailsShown, setIsExecutionDetailsShown] = useState(false);
  const [isCancelable, setIsCancelable] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [scrollToBtnStatus, setScrollToBtnStatus] = useState(null);

  const userId = sessionStorage.getItem("id");
  const executionRoomName = `EXECUTION_STATUS_${flow?.id}`;
  const chatRoomName = `CHAT_STATUS_${chat.id}`;
  const executionSessionId = chatSessionId || sessionStorage.getItem("execution_session_id");

  const userPermissions = useUserPermissions({ userId, flowId: flow.id, chatId: chat.id });

  const messageDateFilter = (
    messages.length == 0 ? "" :
      `AND created_at > ${new Date(messages[messages.length - 1].createdAt).toISOString()}`
  );

  const reqChat = useDetail(`{
    chat {
      settings
    }
  }`, chat.id);

  const reqPrevChat = useQuery(`{
    chats {
      id
      createdAt
      settings
    }
  }`, `(id<>${chat.id} AND flow.id=${flow.id} AND user.id=${userId})`, {
    limit: 1,
    orderBy: "-created_at",
  });

  const reqMessages = useQuery(`{
    chatMessages {
      createdAt
      content
      action
      settings
      type
      isTemplate
      chat {
        id
      }
      templateApplied {
        templateName
      }
      messageChatSuggestions {
        content
        action
        settings
      }
    }
  }`, `(chat_id=${chat.id} ${messageDateFilter})`, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {

      setScrollToBottom(true);

      const newMessages = data.chatMessages
        .filter(m => !m.isTemplate)
          .filter(m => m.settings?.visible ?? true)
            .filter(m => m.type == "BOT_MESSAGE");

      console.log(newMessages);

      // All processes, messages or executions finished with new message, so refreshing only if there are new messages
      if (newMessages.length > 0) {
        setIsLoading(false);
        setMessages(prev => [...prev, ...data.chatMessages]
          .filter(m => m.id)
            .filter((v, i, a) => a.findIndex(t => (t.id === v.id)) === i)
              .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)));
      }

    },
    orderBy: "created_at"
  });

  const reqFlowCount = useCount("flowNode", `(flow.id=${flow.id} AND is_root=true)`);
  const reqCollectionCount = useCount("collection", `(flow.id=${flow.id} AND is_active=true)`);

  const qModels = useGet("/chats/get_llm_models");

  const [callSaveMessage] = useSave(SAVE_CHAT_MESSAGE, {
    onCompleted: () => reqMessages.refetch(),
  });

  const [callSetRead] = useSet(SET_EXECUTION);

  const [callCancelChatAction] = usePost("/chats/cancel_action", {
    onCompleted: () => reqMessages.refetch(),
  });

  const [callSendMessage] = usePost("/chats/send_message", {
    onCompleted: () => reqMessages.refetch(),
    onError: () => reqMessages.refetch()
  });

  const [callSendSuggestion] = usePost("/chats/send_suggestion", {
    onCompleted: () => reqMessages.refetch(),
    onError: (error) => {
      
      if(error.status == 409) {
        toast(l("Hay otra ejecución en curso. Por favor, espera a que termine%PA182"), { type: "error" });
        // Remove last user message that was added as suggestion but wasn't created and set loading to false
        setIsLoading(false);
        setMessages(prev => prev.filter(m => m.id));
      }

      reqMessages.refetch();

    }
  });

  const [callRepeatExecution] = usePost("/chats/repeat_execution", {
    onCompleted: () => reqMessages.refetch()
  });

  const [callRegenerate] = usePost("/chats/regenerate_message", {
    onCompleted: () => reqMessages.refetch(),
    onError: () => reqMessages.refetch()
  });

  const [callExecuteWizard] = usePost("/chats/create_execution_wizard", {
    onCompleted: (data) => {
      callSetRead({ id: data.chat.id, isRead: true });
    },
    onError: (error) => {
      reqMessages.refetch();
    }
  });

  const [callCheckExecution] = usePost("/executions/get_executions", {
    onCompleted: (data) => {
      let details = null;
      if(data?.executions?.length > 0) details = data.executions[0];
      if(details && details.status != executionRef.current?.status)
        onExecutionMessage({
          status: details.status,
          execution_id: details.id,
        });
    },
    onError: () => {
      executionRef.current = null;
    }
  });

  useEffect(() => {
    reqMessages.refetch();
    setTimeout(() => {
      try {
        const promptElement = document.getElementById("chat-prompt")
        promptElement.readOnly = true;
        promptElement.focus()
        setTimeout(() => {
          promptElement.readOnly = false;
        }, 200)
      } catch { }
    }, 450)
  }, [chat.id]);

  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    const templateApplied = (lastMessage?.templateApplied ?? [])[0]?.templateName;
    const actionType = lastMessage?.action?.type;
    if ((templateApplied && templateApplied != "default") || actionType != null)
      setIsCancelable(true);
    else
      setIsCancelable(false);
  }, [messages]);

  useEffect(() => {

    if (selectedNote && chatRef.current && selectedNote?.message?.chat?.id == messages?.[0]?.chat?.id) {

      const messageDiv = document.getElementById(`chat-message-${selectedNote?.message?.id}`);
      if (messageDiv) {
        chatRef.current.scrollTo({
          top: messageDiv.offsetTop - chatRef.current.offsetTop,
          behavior: "smooth",
        });
      }

    }

  }, [selectedNote, messages]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      reqMessages.refetch();
      if(executionRef.current)
        callCheckExecution({ execution_id: executionRef.current.execution_id, include_awaiting: true });
    }, [15 * 1000]);
    return () => clearInterval(intervalId);
  }, []);

  const onChatMessageReceived = ({ data }) => {
    if (data?.session_id && data?.session_id != executionSessionId) return;
    if (data?.execution_id) callSetRead({ id: data.execution_id, isRead: true });
  }

  const onExecutionMessageReceived = ({ data }) => {

    if (["OK", "FAILED", "ABORTED"].includes(data.status)) executionRef.current = null;
    else executionRef.current = data;

    if (data?.session_id && data?.session_id != executionSessionId) return;
    onExecutionMessage(data);

    reqMessages.refetch();

    const executionId = data.execution_id;
    callSetRead({ id: executionId, isRead: true });

  }

  const getMessageObj = (plainText, suggestion) => (
    {
      createdAt: Date.now(),
      content: suggestion?.content ? suggestion.content :
        {
          text: `<p>${plainText.replace("\"", "\\\"")}</p>`,
          plain: `${plainText.replace("\"", "\\\"")}`
        },
      action: {},
      messageSuggestions: [],
      type: "USER_MESSAGE",
    }
  )

  const onChatScroll = () => {
    if (chatRef.current) {
      const scrollTop = chatRef.current.scrollTop
      const scrollHeight = chatRef.current.scrollHeight - chatRef.current.clientHeight
      if (scrollHeight > 400) {
        if (scrollHeight - scrollTop < 500) setScrollToBtnStatus("TO_TOP")
        else setScrollToBtnStatus("TO_BOTTOM")
      } else setScrollToBtnStatus(null)
    }
  }

  const onClickScrollToBtn = () => {
    if (chatRef.current) {
      chatRef.current.scrollTo({
        top: scrollToBtnStatus == "TO_TOP" ? 0 : chatRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }

  const onClickSuggestion = (message, { suggestion, type }) => {

    const actionType = suggestion?.action?.node?.action?.type;

    if (type == "NODE" && actionType == "TRIGGER") {

      const action = suggestion?.action?.node?.action;
      const node = suggestion?.action?.node;
      const messageContent = bodyContentMapper(action.ui_settings.node, node.input_values, "execution_message", action.ui_settings.form);

      callSaveMessage({
        type: "BOT_MESSAGE",
        content: {
          text: `<p>${messageContent}</p>`,
          plain: `${messageContent}`
        },
        action: {},
        settings: {},
        templateName: "",
        isTemplate: false,
        responseRequired: false,
        chat: { id: chat.id },
      })

    }
    else if (type == "NODE") {

      let saveSuggestion = message?.settings ? message.settings.save != false : true
      if (saveSuggestion)
        setMessages([...messages, getMessageObj(null, suggestion)]);

      callSendSuggestion({
        chat_id: chat.id,
        suggestion_id: suggestion.id,
        session_id: executionSessionId,
      });
      setIsLoading(true);

    }
    else if (type == "WIZARD") {
      if (suggestion.processName == "CREATE_PROCESS")
        window.location.href = `/app/studio/${flow.id}/action_form`
      else
        callExecuteWizard({
          node_name: suggestion.processName,
          flow_id: flow.id,
          params: {},
          session_id: executionSessionId,
          chat_id: chat.id,
        });
    }

  }

  const onSendMessage = (data, form) => {

    const message = data.message || "";
    const attachments = data.attachments || [];
    const isWizardSuggestion = data.is_wizard_suggestion || false;

    setScrollToBottom(true)
    form.setFieldValue('message', '');
    form.setFieldValue('attachments', []);
    form.setFieldValue('is_wizard_suggestion', false);
    setMessages([...messages, getMessageObj(typeof message === "string" ? message : "Enviando datos...")]);

    if (!isLoading) {
      callSendMessage({ chat_id: chat.id, message, attachments, is_wizard_suggestion: isWizardSuggestion });
      setIsLoading(true);
    }

  }

  const onClickCancelChatAction = () => {
    callCancelChatAction({ chat_id: chat.id });
    setIsCancelable(false);
  }

  const onClickRegenerateMessage = (message, model) =>
    callRegenerate({ message_id: message.id, model: model, });

  const onClickRepeatExecution = (message_id) =>
    callRepeatExecution({ message_id, session_id: executionSessionId });

  const currentMessage = messages[messages.length - 1];
  const visibleMessages = messages.filter(message => message.settings?.visible ?? true).filter(message => message.isTemplate != true);
  const chatSettings = reqChat.data?.chat?.settings ?? {};
  const prevChat = (reqPrevChat.data?.chats ?? [])[0];
  const botMessage = messages.filter(message => message.isTemplate != true && message.type == "BOT_MESSAGE").slice(-1)[0];

  const { models = [] } = qModels.data || {};
  const { count: processCount } = reqFlowCount.data?.flowNodeCount || {};
  const { count: collectionCount } = reqCollectionCount.data?.collectionCount || {};

  const showModel = userPermissions?.flow?.show_model ?? false;
  const showProcesses = userPermissions?.flow?.show_processes ?? false;

  if (reqMessages.error) return "";

  if (scrollToBottom) {
    setTimeout(() => {
      chatRef.current?.scrollTo({
        top: chatRef.current?.scrollHeight,
        behavior: "smooth",
      });
    }, 150);
    setScrollToBottom(false);
  }

  return <View
    chatRef={chatRef}
    subFormRef={subFormRef}
    notesRef={notesRef}
    flow={flow}
    chat={chat}
    processCount={processCount}
    collectionCount={collectionCount}
    currentChat={currentChat}
    prevChat={prevChat}
    chatSettings={chatSettings}
    executionRoomName={executionRoomName}
    chatRoomName={chatRoomName}
    messages={visibleMessages}
    currentMessage={currentMessage}
    botMessage={botMessage}
    models={models}
    selectedNote={selectedNote}
    scrollToBtnStatus={scrollToBtnStatus}
    removePadding={removePadding}
    showModel={showModel}
    showProcesses={showProcesses}
    isExecutionChat={isExecutionChat}
    isLoading={isLoading}
    isExecutionDetailsShown={isExecutionDetailsShown}
    isDeveloper={isDeveloper}
    isCancelable={isCancelable}
    onClickRegenerateMessage={onClickRegenerateMessage}
    onClickRepeatExecution={onClickRepeatExecution}
    onClickResumeChat={onClickResumeChat}
    onClickSuggestion={onClickSuggestion}
    onClickScrollToBtn={onClickScrollToBtn}
    onSendMessage={onSendMessage}
    onClickCancelChatAction={onClickCancelChatAction}
    onExecutionMessageReceived={onExecutionMessageReceived}
    onChatMessageReceived={onChatMessageReceived}
    onChatScroll={onChatScroll}
    onClickTab={onClickTab}
    setIsExecutionDetailsShown={setIsExecutionDetailsShown}
  />;

});

Chat.displayName = "Chat";

export default Chat;